// src/components/Work.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { GradientMesh } from './ui/Motion';

// Case studies data
const caseStudies = [
  {
    id: 'case1',
    title: 'Legal Lead Generation Infrastructure',
    category: 'LEGALTECH',
    description: 'Directed high-volume legal lead generation ecosystems for personal injury and mass tort campaigns, generating 30-50 signed cases per client monthly across multiple practice areas.',
    tags: ['Paid Media', 'Lead Generation', 'Funnel Optimization', 'Audience Segmentation'],
    gradient: 'primary',
    details: {
      challenge: 'Managing multi-million dollar budgets across 100+ legal client accounts required a reliable system to deliver consistent, high-quality leads in a competitive legal advertising landscape while maintaining compliance.',
      solution: 'I developed a full-funnel lead generation and qualification system using Airtable, Looker Studio, and Google Ads. I connected Airtable directly to Google Ads to train the algorithm on our ideal lead profiles and used Google\'s Customer Match to upload first-party email lists—targeting lookalike audiences while excluding existing ones to increase reach without redundancy. Campaigns were deployed across Google Search, X (formerly Twitter), Reddit, and native platforms, with performance tracking visualized in Looker Studio. These strategies resulted in a measurable lift in both lead volume and quality, consistently generating 30–50 signed cases per client each month, depending on spend.',
      results: [
        { stat: '30+', label: 'Signed Cases Monthly' },
        { stat: '-40%', label: 'Acquisition Cost' },
        { stat: '4.5x', label: 'ROI Improvement' }
      ]
    }
  },
  {
    id: 'case2',
    title: 'Marketing Infrastructure Consultation',
    category: 'ELECTRONICS',
    description: 'Led the transition of LG Africa\'s marketing operations from an external agency model to a fully in-house function, building internal capability and implementing advanced tracking infrastructure to support performance-driven growth.',
    tags: ['Digital Transformation', 'Marketing Operations', 'Tech Infrastructure', 'Performance Tracking'],
    gradient: 'secondary',
    details: {
      challenge: 'LG Africa faced major performance inefficiencies due to over-reliance on external agencies resulting in limited campaign visibility, inconsistent results, high acquisition costs, and no internal capability to scale marketing execution across diverse African markets.',
      solution: 'I led the full transition of LG Africa\'s digital marketing operations in-house. This included building an advanced Meta tracking architecture with custom event mapping, CAPI integration, and cross-domain attribution protocols across product sites. I redesigned their entire audience strategy, developing market-specific customer segments, lookalike models, and strategic retargeting flows. These frameworks supported localized execution across multiple regions while enabling team ownership.',
      results: [
        { stat: '42%', label: 'Decrease in CPA' },
        { stat: '65%', label: 'Improved Conversion' },
        { stat: '85%', label: 'Increase in ROAS' }
      ]
    }
  },
  {
    id: 'case3',
    title: 'Financial Services Marketing System',
    category: 'FINTECH',
    description: 'Built marketing automation infrastructure for financial advisors at Snappy Kraken, combining sophisticated lead generation with nurturing sequences and qualification logic.',
    tags: ['Funnel Architecture', 'CRM Integration', 'Lead Qualification', 'Multi-Platform Advertising'],
    gradient: 'accent',
    details: {
      challenge: 'Financial advisors needed a systematic approach to generate qualified leads while maintaining compliance standards. The previous lead generation process was inefficient with CPLs ranging from $40-65 per lead.',
      solution: 'I launched and optimized Meta advertising campaigns, installing pixel tracking and implementing Conversion API (CAPI) for enhanced data accuracy. I built custom audiences from advisor email lists, then created lookalike segments and exclusion layers to improve reach and relevance. To prevent creative fatigue and improve engagement, I designed and tested multiple variations of ad content, landing pages, and funnel flows.',
      results: [
        { stat: '400%', label: 'CPL Reduction' },
        { stat: '70%+', label: 'First Month Improvement' },
        { stat: '$175k+', label: 'Monthly Recurring Revenue' }
      ]
    }
  }
];

const Work = () => {
  const [expandedCase, setExpandedCase] = useState(null);
  
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  
  const handleCaseClick = (id) => {
    setExpandedCase(expandedCase === id ? null : id);
  };

  return (
    <WorkSection id="work">
      <GradientMesh
        blobs={[
          { size: '34vw', top: '5%', left: '-8%', color: '#06b6d4', opacity: 0.14, blur: 120, dur: 26 },
          { size: '30vw', bottom: '0%', right: '-6%', color: '#6366f1', opacity: 0.14, blur: 120, dur: 30, delay: 5 },
        ]}
      />
      <Container>
        <SectionHeader
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <Eyebrow>Case studies</Eyebrow>
          <Title className="gradient-text-secondary">Selected Work</Title>
          <Divider className="gradient-bg-secondary" />
          <Description>
            My work spans performance marketing, funnel architecture, lead generation, marketing automation, and operation optimization across LegalTech, FinTech, E-commerce, and multinational brands.
          </Description>
        </SectionHeader>
        
        <CaseStudyGrid
          ref={ref}
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {caseStudies.map((caseStudy) => (
            <CaseStudyCard
              key={caseStudy.id}
              as={motion.div}
              variants={itemVariants}
              className="bg-glass"
              onClick={() => handleCaseClick(caseStudy.id)}
              isActive={expandedCase === caseStudy.id}
            >
              <CardHeader className={`gradient-bg-${caseStudy.gradient}`}>
                <CategoryLabel>{caseStudy.category}</CategoryLabel>
              </CardHeader>
              
              <CaseStudyContent>
                <CaseStudyTitle className={`gradient-text-${caseStudy.gradient}`}>
                  {caseStudy.title}
                </CaseStudyTitle>
                <CaseStudyDescription>
                  {caseStudy.description}
                </CaseStudyDescription>
                
                {/* Organized Tag Grid */}
                <TagsGrid>
                  {caseStudy.tags.slice(0, 4).map((tag) => (
                    <Tag key={tag} gradient={caseStudy.gradient}>{tag}</Tag>
                  ))}
                </TagsGrid>
                
                <CaseStudyLink className={`gradient-text-${caseStudy.gradient}`}>
                  {expandedCase === caseStudy.id ? "View Less" : "View Details"}
                  <LinkIcon>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      {expandedCase === caseStudy.id ? (
                        <polyline points="18 15 12 9 6 15"></polyline>
                      ) : (
                        <polyline points="6 9 12 15 18 9"></polyline>
                      )}
                    </svg>
                  </LinkIcon>
                </CaseStudyLink>
              </CaseStudyContent>
              
              <AnimatePresence>
                {expandedCase === caseStudy.id && (
                  <CaseDetails
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <CaseDetailsDivider />
                    <CaseDetailsSection>
                      <CaseDetailsSectionTitle>The Challenge</CaseDetailsSectionTitle>
                      <CaseDetailsSectionContent>
                        {caseStudy.details.challenge}
                      </CaseDetailsSectionContent>
                    </CaseDetailsSection>
                    
                    <CaseDetailsSection>
                      <CaseDetailsSectionTitle>The Solution</CaseDetailsSectionTitle>
                      <CaseDetailsSectionContent>
                        {caseStudy.details.solution}
                      </CaseDetailsSectionContent>
                    </CaseDetailsSection>
                    
                    <CaseDetailsSection>
                      <CaseDetailsSectionTitle>The Results</CaseDetailsSectionTitle>
                      <CaseStatsGrid>
                        {caseStudy.details.results.map((result, idx) => (
                          <CaseStats key={idx}>
                            <CaseStatsNumber className={`gradient-text-${caseStudy.gradient}`}>{result.stat}</CaseStatsNumber>
                            <CaseStatsLabel>{result.label}</CaseStatsLabel>
                          </CaseStats>
                        ))}
                      </CaseStatsGrid>
                    </CaseDetailsSection>
                  </CaseDetails>
                )}
              </AnimatePresence>
            </CaseStudyCard>
          ))}
        </CaseStudyGrid>
        
        <ViewAllButton
          href="https://calendar.app.google/JNzzrs67cJGNb3Vq8"
          as={motion.a}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          Schedule a Strategy Consultation
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z"></path>
          </svg>
        </ViewAllButton>
      </Container>
    </WorkSection>
  );
};

// Styled components
const WorkSection = styled.section`
  padding: 7rem 0;
  position: relative;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 4.5rem 0;
  }
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 2;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0 1.5rem;
  }
`;

const Eyebrow = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #06b6d4;
  margin-bottom: 0.85rem;
`;

const SectionHeader = styled(motion.div)`
  margin-bottom: 4rem;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const Title = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2rem;
  }
`;

const Divider = styled.div`
  width: 6rem;
  height: 4px;
  border-radius: 2px;
  margin: 0 auto 1.5rem;
`;

const Description = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1.125rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1rem;
  }
`;

const CaseStudyGrid = styled.div`
  display: grid;
  gap: 2rem;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const CaseStudyCard = styled.div`
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
  
  ${({ isActive, theme }) => isActive && `
    transform: translateY(-8px);
    box-shadow: ${theme.shadows.lg};
  `}
`;

const CardHeader = styled.div`
  padding: 2rem;
  position: relative;
  height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const CategoryLabel = styled.span`
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.025em;
  text-transform: uppercase;
  color: white;
  background: rgba(255, 255, 255, 0.2);
  align-self: flex-start;
`;

const CaseStudyContent = styled.div`
  padding: 2rem;
`;

const CaseStudyTitle = styled.h3`
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.5rem;
  }
`;

const CaseStudyDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 1.5rem;
`;

// Replace Tags with TagsGrid for better organization
const TagsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

// Update Tag styling to match the screenshot
const Tag = styled.span`
  padding: 0.5rem 0.75rem;
  background: ${({ gradient, theme }) => {
    // Customize based on the case study's gradient
    switch(gradient) {
      case 'primary':
        return 'rgba(99, 102, 241, 0.1)';
      case 'secondary':
        return 'rgba(6, 182, 212, 0.1)';
      case 'accent':
        return 'rgba(245, 158, 11, 0.1)';
      default:
        return 'rgba(255, 255, 255, 0.05)';
    }
  }};
  border-radius: 9999px;
  font-size: 0.875rem;
  color: ${({ gradient, theme }) => {
    // Text color based on gradient
    switch(gradient) {
      case 'primary':
        return '#8D91F7';
      case 'secondary':
        return '#4DD0E1';
      case 'accent':
        return '#F7B955';
      default:
        return theme.colors.textSecondary;
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 36px;
`;

const CaseStudyLink = styled.div`
  display: inline-flex;
  align-items: center;
  font-weight: 600;
  gap: 0.5rem;
  transition: gap 0.3s ease;
  
  &:hover {
    gap: 0.75rem;
  }
`;

const LinkIcon = styled.span`
  display: flex;
  align-items: center;
`;

const CaseDetails = styled(motion.div)`
  padding: 0 2rem 2rem;
  overflow: hidden;
`;

const CaseDetailsDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border};
  margin-bottom: 1.5rem;
`;

const CaseDetailsSection = styled.div`
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const CaseDetailsSectionTitle = styled.h4`
  font-size: 1.125rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  color: ${({ theme }) => theme.colors.text};
`;

const CaseDetailsSectionContent = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CaseStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const CaseStats = styled.div`
  text-align: center;
`;

const CaseStatsNumber = styled.div`
  font-size: 2rem;
  font-weight: 800;
  margin-bottom: 0.5rem;
`;

const CaseStatsLabel = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ViewAllButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${({ theme }) => theme.gradients.primary};
  color: white;
  font-weight: 600;
  font-size: 1rem;
  padding: 1rem 1.75rem;
  border-radius: 9999px;
  margin: 3rem auto 0;
  display: block;
  width: fit-content;
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.4);
  transition: all 0.3s ease;
  
  svg {
    transition: transform 0.3s ease;
  }
  
  &:hover svg {
    transform: translateX(4px);
  }
`;

export default Work;